import { useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'

import CSVIO from 'components/CSVIO'
import ErrorDialog from 'components/ErrorDialog'
import { Flex, FlexContainer } from 'components/Flex'
import ListAction from 'components/ListAction'
import ListFilter from 'components/ListFilter'
import ListItem from 'components/ListItem'
import ListSort from 'components/ListSort'
import Panel from 'components/Panel'
import {
  doc,
  generateId,
  orderBy,
  serverTimestamp,
  useDocs,
  where,
  writeBatch,
} from 'lib/firestore'
import { isMobile } from 'utils'
import { Role } from 'utils/constant'

import LocationDialog from './LocationDialog'

export default function LocationList({ account }) {
  const [data, setData] = useState(null)
  const [sort, setSort] = useState({ field: 'name', order: 'asc' })
  const [filter, setFilter] = useState({ deleted: false })
  const [error, setError] = useState(null)
  const conditions = useMemo(() => {
    const _conditions = []
    Object.entries(filter).forEach(([field, value]) => {
      if (value !== '*') {
        _conditions.push(where(field, '==', value))
      }
    })
    // role 絞り込み中に role ソートしない
    _conditions.push(orderBy(sort.field, sort.order))
    return _conditions
  }, [filter, sort])
  const { items } = useDocs(`accounts/${account.id}/locations`, ...conditions)

  if (account.role === Role.LOCATION_MEMBER.value) {
    return <Redirect to={`accounts/${account.id}/stocks`} />
  }

  const handleChangeSort = field => {
    setSort(current => ({
      field,
      order: current.field !== field || current.order === 'desc' ? 'asc' : 'desc',
    }))
  }

  const handleChangeFilter = (field, value) => {
    if (['true', 'false'].includes(value)) {
      setFilter({ [field]: JSON.parse(value) })
    } else {
      setFilter({ [field]: value })
    }
  }

  if (!items) {
    return (
      <FlexContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </FlexContainer>
    )
  }

  const saveLocations = async values => {
    const batch = writeBatch()
    values.forEach(value => {
      const docId = value.id || generateId(`accounts/${account.id}/locations`)
      const docRef = doc(`accounts/${account.id}/locations/${docId}`)
      const { id, name, category, deleted, ...contact } = value
      const data = { id, name, category, deleted, contact }
      if (!value.id) {
        data.createdAt = serverTimestamp()
      }
      batch.set(docRef, data, { merge: true })
    })
    await batch.commit()
  }

  return (
    <Flex>
      <ListAction>
        <ListFilter
          fields={[
            {
              id: 'deleted',
              name: '状態',
              options: [
                { label: 'アクティブ', value: false },
                { label: '削除済', value: true },
              ],
            },
          ]}
          value={filter}
          onChange={handleChangeFilter}
        />
        {!isMobile && (
          <>
            <div style={{ width: 16 }} />
            <CSVIO
              onChange={saveLocations}
              onError={error => setError(error.message)}
              format={{
                id: { name: 'ID' },
                name: { name: '拠点名（必須）', required: '拠点名は必須項目です' },
                category: { name: '拠点カテゴリ' },
                postalCode: { name: '郵便番号' },
                address: { name: '住所' },
                department: { name: '部署' },
                tel: { name: '電話番号' },
                staff: { name: '担当者名' },
                deleted: {
                  name: '削除する（はい or いいえ）',
                  convert: v => v === 'はい',
                },
              }}
              data={items.map(x => {
                const { contact, ...values } = x
                return { ...values, ...contact, deleted: x.deleted ? 'はい' : 'いいえ' }
              })}
            />
          </>
        )}
      </ListAction>
      <Panel>
        <ListSort
          fields={[
            { id: 'name', name: '拠点名' },
            { id: 'createdAt', name: '作成順' },
          ]}
          value={sort}
          onChange={handleChangeSort}
        />
        {items
          .sort((a, b) => {
            const [x, y] = sort.order === 'asc' ? [1, -1] : [-1, 1]
            if (sort.field !== 'name') {
              return a[sort.field] > b[sort.field] ? x : y
            }

            // WORKAROUND: デフォルト値の設定
            a.category = a.category || ''
            b.category = b.category || ''

            if (a.category === b.category) {
              return a.name > b.name ? x : y
            }
            if (a.category && b.category) {
              return a.category > b.category ? x : y
            }
            return b.category ? x : y
          })
          .map(x => (
            <ListItem
              key={x.id}
              primary={x.name}
              label={x.deleted ? '削除済み' : null}
              onClick={() => setData(x)}
            />
          ))}
      </Panel>
      <Fab
        color="primary"
        style={{ position: 'fixed', right: 16, bottom: 16 }}
        onClick={() => setData({})}
      >
        <AddIcon />
      </Fab>
      {data && <LocationDialog account={account} data={data} onClose={() => setData(null)} />}
      {Boolean(error) && <ErrorDialog error={error} onClose={() => setError(null)} />}
    </Flex>
  )
}
